import { useCallback, useEffect, useMemo, useState } from "react";
import { DragOverlay, useDndMonitor, type DragStartEvent } from "@dnd-kit/core";
import type { POI } from "@poi-planner/shared";
import { useCalendarDndStore } from "../store/calendarDndStore";
import { BlockDragGhost } from "./calendar/BlockDragGhost";
import type { BoardDragData } from "./calendar/dragTypes";
import { DragOverlayCard } from "./DragOverlayCard";
import { accommodationPalettes } from "../theme/colors";

const DRAG_ACTIVE_CLASS = "calendar-drag-active";

interface BoardDragOverlayProps {
  pois: POI[];
}

/**
 * Owns transient drag-overlay state so activating a drag does not rerender the
 * board shell, sidebar, calendar views, and shared modals.
 */
export function BoardDragOverlay({ pois }: BoardDragOverlayProps) {
  const [activeDrag, setActiveDrag] = useState<BoardDragData | null>(null);
  const hasDropPreview = useCalendarDndStore((s) => s.preview !== null);
  const hasDateRangePreview = useCalendarDndStore(
    (s) => s.dateRangePreview !== null,
  );

  const clearDrag = useCallback(() => {
    setActiveDrag(null);
    document.body.classList.remove(DRAG_ACTIVE_CLASS);
  }, []);

  const monitor = useMemo(
    () => ({
      onDragStart: (event: DragStartEvent) => {
        const data = event.active.data.current as BoardDragData | undefined;
        if (!data) return;
        setActiveDrag(data);
        document.body.classList.add(DRAG_ACTIVE_CLASS);
      },
      onDragEnd: clearDrag,
      onDragCancel: clearDrag,
    }),
    [clearDrag],
  );

  useDndMonitor(monitor);

  useEffect(() => () => document.body.classList.remove(DRAG_ACTIVE_CLASS), []);

  const activeDragPOI = useMemo(
    () =>
      activeDrag?.type === "sidebar-poi"
        ? (pois.find((poi) => poi.id === activeDrag.poiId) ?? null)
        : null,
    [activeDrag, pois],
  );

  return (
    <DragOverlay dropAnimation={null}>
      {activeDragPOI ? (
        // Over a column the phantom + snapped readout are the feedback — the
        // card overlay would just cover them.
        hasDropPreview ? null : (
          <DragOverlayCard poi={activeDragPOI} />
        )
      ) : activeDrag?.type === "accommodation" ? (
        <div
          className="flex h-7 min-w-44 cursor-grabbing items-center gap-1.5 rounded-md border px-2 text-xs font-bold opacity-95 shadow-xl"
          style={{
            background: accommodationPalettes[activeDrag.colorIndex]?.header,
            borderColor: accommodationPalettes[activeDrag.colorIndex]?.border,
            color: accommodationPalettes[activeDrag.colorIndex]?.text,
          }}
        >
          <span className="material-symbols-outlined text-base">hotel</span>
          <span className="max-w-48 truncate">{activeDrag.label}</span>
          <span className="ml-auto text-2xs opacity-70">
            {activeDrag.nightCount}n
          </span>
        </div>
      ) : activeDrag?.type === "all-day-note" ? (
        hasDateRangePreview ? null : (
          <div className="flex h-7 min-w-44 cursor-grabbing items-center gap-1.5 rounded-md bg-calendar-all-day-bg-strong px-2 text-xs font-bold text-calendar-all-day-text opacity-95 shadow-xl">
            <span className="material-symbols-outlined text-sm">
              calendar_today
            </span>
            <span className="max-w-48 truncate">{activeDrag.label}</span>
            <span className="ml-auto text-2xs opacity-70">
              {activeDrag.dateCount}d
            </span>
          </div>
        )
      ) : activeDrag?.type === "leg-range" ? (
        hasDateRangePreview ? null : (
          <div className="flex h-6 min-w-40 cursor-grabbing items-center gap-1.5 rounded-full border border-border bg-white px-2.5 text-2xs font-bold opacity-95 shadow-xl">
            <span className="max-w-48 truncate">{activeDrag.label}</span>
            <span className="ml-auto text-2xs opacity-70">
              {activeDrag.dateCount}d
            </span>
          </div>
        )
      ) : activeDrag && activeDrag.type !== "sidebar-poi" ? (
        hasDropPreview ? null : (
          <BlockDragGhost
            label={activeDrag.label}
            spanMinutes={activeDrag.spanMinutes}
            kind={activeDrag.type}
          />
        )
      ) : null}
    </DragOverlay>
  );
}
